import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { ApiCallError, callFunction } from '@/lib/api';
import { usePatientPicker } from '@/hooks/usePatientPicker';
import { answersLabel, priorityChip, receivedEpikriseHeading } from '@/lib/referral/receivedEpikrise';
import { listReceivedMessages } from '@/lib/referral/receivedList';
import { type EpikriseDetail, ReceivedEpikriseDetail } from './ReceivedEpikriseDetail';

// Spor A T1.4 (DIS01/02/03/05-M): "Modtagne epikriser". The inbox shows type,
// patient, sender, date, the priority chip and the first line of the
// recommendation (DIS02/03-M 2.6, DIS01-M 2.4/3.20); a click opens the detail.
// communicate-epikriser keeps every CPR server-side.

export type ReceivedRow = Row;
type Row = {
  id: string;
  exchange_id: string;
  kind: string;
  received_at: string | null;
  letter_identifier: string | null;
  status_code: string | null;
  sender_name: string | null;
  sender_ean: string | null;
  patient_name: string | null;
  patient_display_name: string | null;
  patient_id_kind: 'cpr' | 'erstatnings_cpr' | 'none';
  patient_id: string | null;
  match_status: 'matched' | 'unmatched' | 'manual';
  action_priority: string | null;
  action_priority_raw: string | null;
  recommendation_preview: string | null;
  object_keys: string[];
  corrects_id: string | null;
  corrected_by_id: string | null;
  xsd_ok: boolean | null;
  answers: { id: string; letter_identifier: string; created_at: string; kind: string } | null;
};

const AUTO_REFRESH_MS = 30_000;

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleString('da-DK', { timeZone: 'Europe/Copenhagen', dateStyle: 'short', timeStyle: 'short' });
}

export function AttachPatient({ clinicId, row, onDone }: { clinicId: string; row: Row; onDone: () => void }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: patients } = usePatientPicker(clinicId, search);
  async function attach(patientId: string) {
    setBusy(true);
    setError(null);
    try {
      await callFunction('communicate-epikriser', { action: 'attach', clinic_id: clinicId, epikrise_id: row.id, patient_id: patientId });
      onDone();
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : t('medcom.epikriser.attach_failed', 'Epikrisen kunne ikke knyttes til patienten.'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-2 rounded-md border border-ds-amber-line bg-ds-amber-tint p-3 text-sm" data-testid="epikrise-attach">
      <p className="font-medium">{t('medcom.epikriser.attach_title', 'Knyt epikrisen til en patient (kun admin)')}</p>
      <input
        className="w-full rounded-md border border-ds-field px-3 py-1.5 text-sm"
        placeholder={t('medcom.epikriser.attach_search', 'Søg patient')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {error && <p role="alert" className="text-ds-red-ink">{error}</p>}
      <ul className="max-h-40 space-y-1 overflow-auto">
        {(patients ?? []).map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-2">
            <span>{`${p.first_name} ${p.last_name}`}</span>
            <Button type="button" size="sm" variant="secondary" disabled={busy} onClick={() => void attach(p.id)}>
              {t('medcom.epikriser.attach', 'Knyt')}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ReceivedEpikriserPanel({
  clinicId,
  kind,
  patientId,
  refreshKey,
  canAttach = false,
}: {
  clinicId: string;
  kind?: string;
  patientId?: string;
  refreshKey?: number;
  canAttach?: boolean;
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<EpikriseDetail | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await listReceivedMessages<Row>(clinicId, patientId);
      if (mine !== seq.current) return;
      setRows(result.epikriser.filter((r) => r.kind !== 'XDIS13' && (!kind || r.kind === kind)));
      setLoadedAt(new Date());
    } catch (err) {
      if (mine !== seq.current) return;
      setError(err instanceof ApiCallError ? err.message : t('medcom.epikriser.load_failed', 'Epikriser kunne ikke hentes.'));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [clinicId, kind, patientId, t]);

  // A new tab (kind) or patient never shows the previous list while loading.
  useEffect(() => {
    setRows(null);
    setOpenId(null);
    setDetail(null);
  }, [clinicId, kind, patientId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible' && !openId) void load();
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load, openId]);

  const openDetail = useCallback(
    async (exchangeId: string) => {
      setOpenId(exchangeId);
      setDetail(null);
      setDetailError(null);
      setDetailBusy(true);
      try {
        setDetail(
          await callFunction<Record<string, string>, EpikriseDetail>('communicate-epikriser', {
            action: 'detail',
            clinic_id: clinicId,
            exchange_id: exchangeId,
          }),
        );
      } catch (err) {
        setDetailError(err instanceof ApiCallError ? err.message : t('medcom.epikriser.detail_failed', 'Epikrisen kunne ikke vises.'));
      } finally {
        setDetailBusy(false);
      }
    },
    [clinicId, t],
  );

  const openRow = rows?.find((r) => r.exchange_id === openId) ?? null;

  return (
    <section className="space-y-3 rounded-md border border-ds-line p-4" data-testid="received-epikriser">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          {t('medcom.epikriser.title', 'Modtagne epikriser')}
          {kind ? ` (${kind})` : ''}
        </h3>
        <div className="flex items-center gap-2">
          {loadedAt && (
            <span className="text-xs text-ds-muted">
              {t('medcom.epikriser.loaded_at', 'Opdateret')} {loadedAt.toLocaleTimeString('da-DK', { timeZone: 'Europe/Copenhagen' })}
            </span>
          )}
          <Button type="button" variant="secondary" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? t('medcom.epikriser.refreshing', 'Opdaterer…') : t('medcom.epikriser.refresh', 'Opdatér')}
          </Button>
        </div>
      </div>
      {error && <p role="alert" className="text-sm text-ds-red-ink">{error}</p>}
      {rows === null && loading && (
        <div className="flex flex-col gap-s3" aria-busy>
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      )}
      {rows && rows.length === 0 && (
        <p className="text-sm text-ds-muted">{t('medcom.epikriser.empty', 'Ingen modtagne epikriser.')}</p>
      )}
      {rows && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="received-epikriser-inbox">
            <thead className="text-xs text-ds-muted">
              <tr>
                <th className="py-1 pr-2">{t('medcom.epikriser.col_type', 'Type')}</th>
                <th className="py-1 pr-2">{t('medcom.epikriser.col_patient', 'Patient')}</th>
                <th className="py-1 pr-2">{t('medcom.epikriser.col_sender', 'Afsender')}</th>
                <th className="py-1 pr-2">{t('medcom.epikriser.col_date', 'Modtaget')}</th>
                <th className="py-1 pr-2">{t('medcom.epikriser.col_priority', 'Prioritet')}</th>
                <th className="py-1">{t('medcom.epikriser.col_recommendation', 'Anbefaling om opfølgning hos egen læge')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const chip = priorityChip(row.action_priority ?? row.action_priority_raw);
                return (
                  <tr
                    key={row.id}
                    className={`cursor-pointer border-t border-ds-line align-top hover:bg-ds-row-hover   ${openId === row.exchange_id ? 'bg-ds-teal-tint ' : ''}`}
                    onClick={() => void openDetail(row.exchange_id)}
                    data-testid="received-epikrise-row"
                  >
                    <td className="py-1.5 pr-2">
                      <span className="font-medium">{receivedEpikriseHeading(row.kind)}</span>
                      {row.status_code === 'rettetbrev' && (
                        <span className="ml-1 rounded bg-ds-red-tint px-1.5 py-0.5 text-[10px] font-bold uppercase text-ds-red-ink">{t('medcom.epikriser.rettet_chip', 'Rettet')}</span>
                      )}
                      {row.corrected_by_id && (
                        <span className="ml-1 rounded bg-ds-neutral-tint px-1.5 py-0.5 text-[10px] font-medium text-ds-text">{t('medcom.epikriser.replaced_chip', 'Erstattet')}</span>
                      )}
                      {row.object_keys.length > 0 && (
                        <span className="ml-1 text-xs text-ds-muted">📎 {row.object_keys.length}</span>
                      )}
                      {row.answers && <div className="text-xs text-ds-teal">{answersLabel(row.answers.letter_identifier, row.answers.created_at)}</div>}
                    </td>
                    <td className="py-1.5 pr-2">
                      {row.patient_display_name ?? row.patient_name ?? '–'}
                      {row.patient_id_kind === 'erstatnings_cpr' && (
                        <span className="ml-1 rounded bg-ds-amber-tint px-1.5 py-0.5 text-[10px] font-medium text-ds-amber-ink">Erstatnings-CPR</span>
                      )}
                      {!row.patient_id && (
                        <div className="text-xs text-ds-amber-ink">{t('medcom.epikriser.unmatched', 'Ikke knyttet til en patient')}</div>
                      )}
                    </td>
                    <td className="py-1.5 pr-2">{row.sender_name ?? row.sender_ean ?? '–'}</td>
                    <td className="py-1.5 pr-2 whitespace-nowrap">{formatTime(row.received_at)}</td>
                    <td className="py-1.5 pr-2">
                      {chip ? (
                        <span className={`rounded px-2 py-0.5 text-xs font-semibold ${chip.className}`} title={chip.meaning}>{chip.label}</span>
                      ) : (
                        <span className="text-xs text-ds-muted">{t('medcom.epikriser.no_priority', 'Ingen markering')}</span>
                      )}
                    </td>
                    <td className="py-1.5 text-xs">{row.recommendation_preview ?? ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {openId && (
        <div className="space-y-2">
          <div className="flex justify-end">
            <Button type="button" size="sm" variant="secondary" onClick={() => { setOpenId(null); setDetail(null); }}>
              {t('medcom.epikriser.close', 'Luk')}
            </Button>
          </div>
          {canAttach && openRow && !openRow.patient_id && (
            <AttachPatient clinicId={clinicId} row={openRow} onDone={() => void load()} />
          )}
          {detailBusy && <p className="text-sm text-ds-muted">{t('medcom.epikriser.loading', 'Henter epikrisen…')}</p>}
          {detailError && <p role="alert" className="text-sm text-ds-red-ink">{detailError}</p>}
          {detail && <ReceivedEpikriseDetail clinicId={clinicId} detail={detail} onOpenExchange={(id) => void openDetail(id)} />}
        </div>
      )}
    </section>
  );
}
